import { Box, Grid, Typography } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import TextField from "../text-field/text-field";
import theme from "@styles/theme";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import {
    getExtensionFromName,
    getMediaTypeFromExtension,
    getMediaTypeFromMimeType,
    hasFileExtension,
} from "@lib/mime/mime";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import { Divider, FormControl, IconButton, InputLabel, MenuItem, Select, TextField as MuiTextField } from "@mzc-pdc/ui";
import { IconCancel } from "@mzc-cloudplex/icons";
import convertToByte from "@cores/convertToByte";
import { MEDIA_TYPES, UPLOAD_FILE_LOCATION_TYPE } from "@constants";

const UploadAssetItemIndividual = ({
    files,
    elements,
    removeSelectedFile,
    disabled,
    onChangeFileName,
    onChangeLabel,
    onChangeLanguage,
    onChangeCaptionKind,
    languages,
    validationErrors,
}) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ width: "100%" }}>
            {Object.keys(files).map((key, idx) => {
                return files[key]?.map((file, index) => {
                    return (
                        <>
                            <Grid
                                container
                                flexDirection={`row`}
                                justifyContent={`start`}
                                alignItems={`center`}
                                flexWrap={`nowrap`}
                                mt={index === 0 && idx === 0 ? 0 : 3}
                            >
                                <AssetThumbnail
                                    variant={"table"}
                                    src={file.accessUrl ?? null}
                                    minWidth={40}
                                    margin={"0 18px 0 0"}
                                    width={40}
                                    height={40}
                                    asset={{
                                        mediaType: getMediaTypeFromExtension(getExtensionFromName(file.name)),
                                    }}
                                />
                                <AssetTypeFlag
                                    width={22}
                                    height={16}
                                    type={getMediaTypeFromExtension(getExtensionFromName(file.name))}
                                />

                                <Grid container mr={1} sx={{ flexBasis: "527px", flex: 1 }} flexDirection={"row"}>
                                    <Grid item>
                                        {getMediaTypeFromExtension(getExtensionFromName(file.name)) ===
                                            MEDIA_TYPES.CAPTION && (
                                            <FormControl sx={{ mr: 1, width: "200px" }}>
                                                <InputLabel id="demo-simple-select-required-label">Kind</InputLabel>
                                                <Select
                                                    value={elements[key][index].kind}
                                                    id="simple-select-required"
                                                    label="Kind "
                                                    placeholder={"종류 선택"}
                                                    disabled={disabled}
                                                    labelId="simple-select-required-label"
                                                    onChange={(e) => {
                                                        onChangeCaptionKind(key, index, e.target.value);
                                                    }}
                                                    size="medium"
                                                >
                                                    <MenuItem value={"CAPTION"}>{t(`common::label::Caption`)}</MenuItem>
                                                    <MenuItem value={"SUBTITLE"}>
                                                        {t(`common::label::Subtitle`)}
                                                    </MenuItem>
                                                    <MenuItem value={"DESCRIPTION"}>
                                                        {t(`common::label::Description`)}
                                                    </MenuItem>
                                                    <MenuItem value={"CHAPTER"}>{t(`common::label::Chapter`)}</MenuItem>
                                                    <MenuItem value={"METADATA"}>
                                                        {t(`common::label::Metadata`)}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Grid>
                                    <Grid item flex={1}>
                                        <TextField
                                            variant={"outlined"}
                                            fullWidth
                                            value={file.displayName}
                                            error={validationErrors[`elements-${key}-name`] ? "warning" : ""}
                                            helperText="Asset name is empty. Please enter asset name."
                                            disabled={disabled}
                                            sx={{
                                                ".MuiInputBase-input": { border: "0px" },
                                            }}
                                            onChange={(e) => onChangeFileName(key, index, e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ width: theme.spacing(10) }}>
                                    <Typography
                                        component={`span`}
                                        variant={"body3"}
                                        color={theme.palette.secondary[600]}
                                    >
                                        {hasFileExtension(file.name) ? getExtensionFromName(file.name) : "None"}
                                    </Typography>
                                </Box>
                                {file.size !== undefined && (
                                    <Divider flexItem orientation={`vertical`} sx={{ margin: `12px 10px` }} />
                                )}
                                <Box sx={{ width: theme.spacing(12.5) }}>
                                    <Typography
                                        component={`span`}
                                        variant={"body3"}
                                        color={theme.palette.secondary[600]}
                                    >
                                        {convertToByte(elements[key][index]?.size, true)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <IconButton
                                        circled
                                        onClick={(e) => removeSelectedFile(key, index)}
                                        disabled={disabled}
                                    >
                                        <IconCancel size={16} htmlColor={theme.palette.secondary[600]} />
                                    </IconButton>
                                </Box>
                            </Grid>

                            {getMediaTypeFromExtension(getExtensionFromName(file.name)) === MEDIA_TYPES.CAPTION && (
                                <Grid container pl={10.5} pr={"233px"} mt={2.5} flexDirection={"row"} gap={1.5}>
                                    <MuiTextField
                                        value={elements[key][index].label}
                                        disabled={disabled}
                                        sx={{ width: "266px" }}
                                        variant={"outlined"}
                                        label={"Label"}
                                        placeholder={"라벨 입력"}
                                        onChange={(e) => {
                                            onChangeLabel(key, index, e.target.value);
                                        }}
                                    />
                                    <FormControl
                                        sx={{
                                            width: 266,
                                        }}
                                    >
                                        <InputLabel id="demo-simple-select-required-label">Language</InputLabel>
                                        <Select
                                            id="demo-simple-select-required"
                                            label="Language "
                                            placeholder={"언어 선택"}
                                            disabled={disabled}
                                            labelId="demo-simple-select-required-label"
                                            onChange={(e) => {
                                                const codes = e.target.value.split("-");
                                                onChangeLanguage(key, index, codes);
                                            }}
                                            size="medium"
                                            defaultValue={`${elements[key][index].language}-${elements[key][index].country}`}
                                        >
                                            {languages?.map((el) => (
                                                <MenuItem
                                                    key={`upload-asset-item-individual-language-${el.code}`}
                                                    value={el.code}
                                                >
                                                    {el.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </>
                    );
                });
            })}
        </Box>
    );
};

export default UploadAssetItemIndividual;
